import { useEffect, useState } from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, IconButton, InputLabel, MenuItem, Select, TextField, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { StorePurchasingInfo } from "../models/StorePurchasingInfo";
import { config } from "../../../authConfig";
import useFetchMsal from "../../../Common/UI/hook/useFetchMsal";
import { LoadingIndicator } from "../../../Common/UI/components/Loading";

interface AddPurchasingDialogProps {
  isOpen: boolean;
  onClose: () => void;
  refetch: () => void;
  initialFormData: StorePurchasingInfo;
  locationCode: string;
}

interface AccountData {
  id: number;
  name: string;
  vendorType: AccountType
}

enum AccountType {
  FEDEX = 'Fedex',
  GROCERY = 'Grocery',
  PRODUCE = 'Produce'
}

export default function AddPurchasingDialog({
  isOpen,
  onClose,
  refetch,
  initialFormData,
  locationCode
}: AddPurchasingDialogProps) {
  const [areAccountsLoading, setAreAccountsLoading] = useState<boolean>(true);
  const [accountsData, setAccountsData] = useState<AccountData[]>([]);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [formData, setFormData] = useState<StorePurchasingInfo>(initialFormData);
  const execute = useFetchMsal();

  useEffect(() => {
    execute('GET', `${config.rootAPIUrl}${config.rootAPIList.purchasingAccounts}`)
      .then(result => {
        setAccountsData(result);
      })
      .catch(error => {
        console.error(error);
      })
      .finally(() => {
        setAreAccountsLoading(false);
      })
  }, []);

  useEffect(() => {
    if (Object.values(initialFormData).every(val => !!val === false)) {
      setIsEditing(false);
      setFormData({
        ...initialFormData,
        locationCode
      });
    } else {
      setIsEditing(true);
      setFormData(initialFormData);
    }
  }, [initialFormData]);
  
  const submitPurchasingInfo = () => {
    if (!isEditing) {
      execute("POST", `${config.rootAPIUrl}${config.rootAPIList.postPurchasing}`, formData)
        .then(result => {
          console.log(result);
          refetch();
        })
        .catch((error) => {
          console.log(error);
        });
    } else {
      if (!initialFormData?.id) return;
      const url = config.rootAPIList.putPurchasing.replace('{id}', `${initialFormData.id}`);
      execute("PUT", `${config.rootAPIUrl}${url}`, formData)
        .then(async (result) => {
          console.log(result);
          refetch();
        })
        .catch((error) => {
          console.log(error);
        });
    }
  }
  
  return (
    <Dialog open={isOpen} onClose={onClose}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <DialogTitle>{isEditing ? 'Edit' : 'Add'} Purchasing Info</DialogTitle>
        <IconButton
          size="small"
          disableRipple
          sx={{
            "&:hover": {
              border: "1px solid black",
              borderRadius: "3px",
            },
            marginRight: "20px",
          }}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <LoadingIndicator show={areAccountsLoading}>
        <DialogContent>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              rowGap: "16px",
            }}
          >
            <Tooltip title="Used for outbound store shipments via FedEx" placement="right">
              <TextField
                type="text"
                label="FedEx Account Number"
                value={formData.fedexAccountNumber || ''}
                onChange={(e) => {
                  if (/^\d*$/.test(e.target.value) && e.target.value.length < 10) {
                    setFormData((prev) => {
                      return {
                        ...prev,
                        fedexAccountNumber: e.target.value
                      };
                    });
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "." || e.key === "e" || e.key === "-") {
                    e.preventDefault(); // Block decimal point, exponential, and negative keys
                  }
                }}
              />
            </Tooltip>

            <FormControl fullWidth>
              <InputLabel id="GroceryVendorNameLabel">Grocery Vendor Name</InputLabel>
              <Select
                labelId="GroceryVendorNameLabel"
                value={formData.groceryVendorName || ''}
                onChange={(e) => {
                  setFormData((prev) => {
                    return {
                      ...prev,
                      groceryVendorName: e.target.value
                    }
                  });
                }}
              >
                {accountsData.filter(d => d.vendorType === AccountType.GROCERY).map((acc, index) => (
                  <MenuItem value={acc.name} key={acc.id + acc.name}>{acc.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              type="text"
              label="Grocery Vendor Account"
              value={formData.groceryVendorNumber || ''}
              onChange={(e) => {
                setFormData((prev) => {
                  return {
                    ...prev,
                    groceryVendorNumber: e.target.value
                  };
                });
              }}
              disabled={!formData.groceryVendorName}
            />

            <FormControl fullWidth>
              <InputLabel id="ProduceVendorNameLabel">Produce Vendor Name</InputLabel>
              <Select
                labelId="ProduceVendorNameLabel"
                value={formData.produceVendorName || ''}
                onChange={(e) => {
                  setFormData((prev) => {
                    return {
                      ...prev,
                      produceVendorName: e.target.value
                    }
                  });
                }}
              >
                {accountsData.filter(d => d.vendorType === AccountType.PRODUCE).map((acc, index) => (
                  <MenuItem value={acc.name} key={acc.id + acc.name}>{acc.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              type="text"
              label="Produce Vendor Account"
              value={formData.produceVendorNumber || ''}
              onChange={(e) => {
                setFormData((prev) => {
                  return {
                    ...prev,
                    produceVendorNumber: e.target.value
                  };
                });
                
              }}
              disabled={!formData.produceVendorName}
            />
          </Box>
        </DialogContent>
      </LoadingIndicator>
      <DialogActions>
        <Button sx={{ bgcolor: "secondary.main" }} onClick={onClose}>
          Cancel
        </Button>
        <Button
          onClick={submitPurchasingInfo}
          disabled={
            !formData.fedexAccountNumber.trim() &&
            !formData.groceryVendorName &&
            !formData.groceryVendorNumber &&
            !formData.produceVendorName &&
            !formData.produceVendorNumber
          }
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}
